import React, { useCallback } from "react";
import { Flex, Text, FileUpload, Card, ErrorMessage } from "@jsq/ds/core";
import { DocumentUploadErrorStyle, fullWidthFileUploadStyles } from "./styles_vanilla.css";

export type PayeeDocumentUploadProps = {
  /**
   * Title for the upload component.
   * Default is "Upload invoice"
   */
  title?: string;

  /**
   * Handler called after a file is selected
   */
  onFileSelected: (file: File) => void;

  /**
   * Handler called when the file is deleted
   */
  onFileDeleted?: () => void;

  /**
   * Error message to display under FileUpload component
   */
  error?: string;

  /**
   * Optional initial file
   */
  initialFile?: File;
};

type FileObject = {
  status: string;
  file: File;
};

const PayeeDocumentUpload: React.FC<PayeeDocumentUploadProps> = ({
  title = "Upload invoice",
  onFileSelected,
  error,
  initialFile,
  onFileDeleted,
}) => {
  const handleFileSelected = useCallback(
    (fileObjs: FileObject[]) => {
      if (fileObjs.length > 0 && fileObjs[0].status === "accepted") {
        const file = fileObjs[0].file;
        onFileSelected(file);
      }
    },
    [onFileSelected],
  );

  return (
    <Card>
      <Flex flexDirection="column" gap={16}>
        <Text variant="h3">{title}</Text>
        <FileUpload
          uploadMessage="Choose file"
          acceptedFileTypes={[".jpg", ".png", ".pdf"]}
          canAllowMultiple={false}
          displayAcceptedFileTypes={true}
          guidanceMessage=""
          alwaysShowGuidance={false}
          onFileSelected={handleFileSelected}
          size="small"
          showFileList={true}
          data-cy="file-to-upload"
          initialFiles={
            initialFile
              ? [{ file: initialFile, status: "accepted", uuid: initialFile.name }]
              : []
          }
          onDelete={onFileDeleted}
          data-testid="UploadPayeeDocument"
          className={fullWidthFileUploadStyles}
        />
        {error && <ErrorMessage className={DocumentUploadErrorStyle}>{error}</ErrorMessage>}
      </Flex>
    </Card>
  );
};

export default PayeeDocumentUpload;
